import { mediaManagerServerApi } from './mediaManagerServerApi';

/**
 * Files that have been downloaded but are not in the library yet.
 *
 * Its own service file rather than part of the movie's, because none of
 * this belongs to a media type: a file on disk is not a film or an
 * episode until somebody says which it is, and that is the whole
 * question this asks.
 *
 * Scanning and placing are queued on the local worker - only that
 * machine has the files, and converting one takes minutes at best - so
 * both hand back a job to poll. Approving and rejecting answer at once:
 * each is one file being deleted, and whoever clicked should be told
 * what happened while they are still looking at it.
 */
export const extendedApiSlice = mediaManagerServerApi.injectEndpoints({
  endpoints: (builder) => ({
    getIncomingFiles: builder.query<any, boolean | void>({
      // Re-fetched by the page rather than invalidated by tags: it polls
      // anyway while a conversion runs, so a tag on the shared base
      // slice would be a second mechanism doing the same job.
      //
      // skipped asks for the ones set aside instead of the ones waiting.
      // They are asked for explicitly rather than mixed in, because the
      // point of skipping something is not to see it.
      query: (skipped) =>
        skipped ? 'core/incoming-files/?skipped=1' : 'core/incoming-files/',
    }),

    scanIncomingFiles: builder.mutation<any, void>({
      query: () => ({
        url: 'core/incoming-files/scan/',
        method: 'POST',
      }),
    }),

    placeIncomingFile: builder.mutation<any, any>({
      // The answers travel with it: what it is, and what to call it.
      // None of that can be read off a download reliably, and a wrong
      // guess puts a file where nobody will look for it again.
      query: ({ id, ...answers }) => ({
        url: `core/incoming-files/${id}/place/`,
        method: 'POST',
        body: answers,
      }),
    }),

    approveIncomingFile: builder.mutation<any, any>({
      // The conversion has been watched and is good, so the original
      // goes. The only thing here that destroys anything.
      query: (id) => ({
        url: `core/incoming-files/${id}/approve/`,
        method: 'POST',
      }),
    }),

    rejectIncomingFile: builder.mutation<any, any>({
      // The conversion is no good: it comes back out and the download
      // stays. The row goes back to waiting.
      query: (id) => ({
        url: `core/incoming-files/${id}/reject/`,
        method: 'POST',
      }),
    }),

    ignoreIncomingFile: builder.mutation<any, any>({
      // Sets one aside. The file is not touched - it stays where it was
      // found - and the row is kept, which is what stops the next scan
      // offering it again.
      query: (id) => ({
        url: `core/incoming-files/${id}/ignore/`,
        method: 'POST',
      }),
    }),

    undoIncomingFile: builder.mutation<any, any>({
      // The other half of moving one, and it was missing: a file put
      // somewhere wrong had no way back. The row said placed, the page
      // showed only what was waiting, and a rescan skipped the file
      // because a row already existed for its path - so it sat in the
      // library under the wrong name, invisible to everything that
      // could have fixed it.
      query: (id) => ({
        url: `core/incoming-files/${id}/undo/`,
        method: 'POST',
      }),
    }),

    restoreIncomingFile: builder.mutation<any, any>({
      // Puts a skipped one back. Skipping used to be a one-way door:
      // the list excluded those rows and nothing could bring one back,
      // so a link one click from the primary action permanently hid a
      // file from the only page that offers it.
      query: (id) => ({
        url: `core/incoming-files/${id}/restore/`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetIncomingFilesQuery,
  useLazyGetIncomingFilesQuery,
  useScanIncomingFilesMutation,
  usePlaceIncomingFileMutation,
  useApproveIncomingFileMutation,
  useRejectIncomingFileMutation,
  useIgnoreIncomingFileMutation,
  useRestoreIncomingFileMutation,
  useUndoIncomingFileMutation,
} = extendedApiSlice;
